/**
 * effectiveSectionOrder — single source of truth for the ordered
 * section-id list that drives TOC, body CSS order, and modern/legacy
 * routing. Mirrors the resolution chain in SiteRagReportView but is
 * pure + side-effect free so tests and export can share it.
 */
import type { ReportTypeId } from '../config/reportTypes';
import { getSectionsForType, stripRetiredSectionIds } from '../config/reportSections';
import { subSectionsForParent } from '../config/rendererSectionRegistry';
import { isCustomBriefQuestionSectionId } from './customBriefSections';
import type { ReportTemplate } from '../types/reportTemplate';
import type { GridLedStandbyPlanningReportInput } from './generationImpact/gridLedStandbyPlanningReportContent';
import {
  projectTemplateSectionIds,
  resolveTemplateSectionIds,
} from './effectiveTemplateConfig';

/** Canonical section ids for a report type (registry order). */
export function getCanonicalSectionIds(reportType: ReportTypeId | undefined | null, report?: GridLedStandbyPlanningReportInput | null): string[] {
  return getSectionsForType(reportType, report).map(s => s.sectionId);
}

/**
 * Filter a template's sectionOrder to ids valid for `reportType`, then
 * append any canonical ids the template omitted (preserving registry
 * order). Prevents feasibility template ids from landing on power reports.
 *
 * @deprecated Prefer projectTemplateSectionIds from effectiveTemplateConfig.
 */
export function projectTemplateSectionOrder(
  reportType: ReportTypeId | undefined | null,
  templateSectionIds: readonly string[] | undefined,
  reportSectionOrder?: readonly string[],
): string[] {
  return projectTemplateSectionIds(reportType, templateSectionIds, reportSectionOrder);
}

export interface TemplateOrderInput {
  sectionOrder?: string[];
  sections?: Array<{ sectionId: string }>;
  dynamicOrder?: boolean;
  /** RTU-4 — full template for sectionsByReportType resolution. */
  sectionsByReportType?: ReportTemplate['sectionsByReportType'];
}

export interface EffectiveSectionOrderInput {
  reportType: ReportTypeId | undefined | null;
  sectionOrder?: string[];
  template?: TemplateOrderInput | ReportTemplate | null;
  report?: GridLedStandbyPlanningReportInput | null;
}

function asFullTemplate(template: TemplateOrderInput | ReportTemplate): ReportTemplate | null {
  if (!template) return null;
  if ('id' in template && typeof template.id === 'string') {
    return template as ReportTemplate;
  }
  // Partial template shape from callers — wrap minimally for resolver
  return {
    id: '',
    name: '',
    scope: 'user',
    sections: (template.sections ?? []) as ReportTemplate['sections'],
    headingOverrides: {},
    isDefault: false,
    createdAt: '',
    updatedAt: '',
    sectionOrder: template.sectionOrder,
    dynamicOrder: template.dynamicOrder,
    sectionsByReportType: template.sectionsByReportType,
  };
}

/** Insert renderer sub-section ids after their parent and any selected
 * planning basis that the canonical registry places immediately after Intent. */
export function augmentSectionOrderWithSubSections(ids: readonly string[]): string[] {
  const out = [...ids];
  const seen = new Set(out);
  for (let i = 0; i < out.length; i++) {
    const parentId = out[i]!;
    const planningFollowsIntent = parentId === 'projectIntentOverview' && out[i + 1] === 'gridLedStandbyPlanning';
    for (const sub of subSectionsForParent(parentId)) {
      if (seen.has(sub.id)) continue;
      out.splice(i + (planningFollowsIntent ? 2 : 1), 0, sub.id);
      seen.add(sub.id);
      i++;
    }
  }
  return out;
}

/**
 * Resolve the effective visual section order for a report.
 * When a template with dynamicOrder !== false is supplied, project
 * report ids through the type-filtered template order (TOR-2).
 */
export function resolveEffectiveSectionOrder(input: EffectiveSectionOrderInput): string[] {
  const { reportType, sectionOrder, template, report } = input;

  const reportIds = stripRetiredSectionIds(
    sectionOrder && sectionOrder.length > 0
      ? projectTemplateSectionIds(reportType, sectionOrder, sectionOrder, report)
      : getCanonicalSectionIds(reportType, report),
  );

  const fullTemplate = template ? asFullTemplate(template) : null;
  const templateIds = fullTemplate
    ? resolveTemplateSectionIds(fullTemplate, reportType, sectionOrder, report)
    : undefined;

  if (templateIds && fullTemplate && fullTemplate.dynamicOrder !== false) {
    const ordered = stripRetiredSectionIds([...templateIds]);
    const seen = new Set(ordered);
    for (const id of reportIds) {
      if (seen.has(id)) continue;
      // A selected planning basis is absent from ordinary template schemas.
      // Backfill it beside Intent/Context, ahead of the existing sub-sections;
      // preserve any explicitly selected template position and all other ids.
      const planningAnchor = id === 'gridLedStandbyPlanning'
        ? ordered.findIndex(sectionId => sectionId === 'projectIntentOverview' || sectionId === 'projectContext')
        : -1;
      if (planningAnchor >= 0) ordered.splice(planningAnchor + 1, 0, id);
      else ordered.push(id);
      seen.add(id);
    }
    return augmentSectionOrderWithSubSections(stripRetiredSectionIds(ordered));
  }

  return augmentSectionOrderWithSubSections(stripRetiredSectionIds([...reportIds]));
}

/** Section ids whose presence routes a report through the modern per-type router. */
export const TYPE_SENTINELS: Partial<Record<ReportTypeId, string>> = {
  // #34 P2 STAGE B — feasibility now routes through NewTypeSectionsRouter. The
  // sentinel `gridStudy` is the first feasibility-exclusive study id in the
  // canonical REPORT_SECTIONS order (executiveSummary / projectIntentOverview
  // are universal), so a fresh OR an archived feasibility report (whose
  // sectionOrder, or the canonical fallback, always carries `gridStudy`) routes
  // modern. The 5 duplicating study sections (gridStudy / capacityCreditDeepDive
  // / transportStudy / connectivityStudy / riskAssessment) are lifted into
  // NEW_SECTION_REGISTRY; the non-duplicating inline-only sections (btmOverview /
  // sitingNotes / climateSevereWeather / radarChart / lcoeChart) stay ALWAYS-ON
  // inline (no registry component → never gated on !hasModernSections, else the
  // router would silently drop them).
  feasibility: 'gridStudy',
  // STAGE 5.2 commit 2 — `gridCapacity` left the power section set (it is a
  // feasibility section now), so the sentinel is the first power-only section.
  'power-availability': 'powerStrategyContext',
  'water-resource': 'supplyAvailability',
  'test-fit': 'parcelOverview',
  interconnection: 'fiberRings',
  'supply-chain-risk': 'procurementOverview',
  environmental: 'nepaScreening',
  'public-sentiment': 'executive',
  'custom-brief': 'projectContext',
  // The LIVE Go/No-Go Decision Guide — `execDecisionBand` is first in the
  // canonical order, so a fresh (never-generated) exec-summary report STILL
  // routes through NewTypeSectionsRouter via the sentinel-in-canonical-order
  // path (hasModernSectionsForReport falls back to getCanonicalSectionIds).
  // This guarantees the live guide always mounts even with no sectionOrder /
  // no generate step.
  'executive-summary': 'execDecisionBand',
  // FIN-2 — `decisionBand` is financial-only, so its presence in the section
  // order routes the Financial Viability report through NewTypeSectionsRouter.
  financial: 'decisionBand',
};

/**
 * True when the report should route through NewTypeSectionsRouter
 * instead of legacy aggregate blocks. Uses the same fallback as TOC/body
 * when sectionOrder is absent.
 */
export function hasModernSectionsForReport(
  report: { reportType?: ReportTypeId | null; sectionOrder?: string[] },
): boolean {
  const type = report.reportType;
  if (!type) return false;
  const sentinel = TYPE_SENTINELS[type];
  if (!sentinel) return false;
  const order = report.sectionOrder && report.sectionOrder.length > 0
    ? report.sectionOrder
    : getCanonicalSectionIds(type);
  if (['feasibility', 'water-resource', 'power-availability'].includes(type) && order.includes('waterScreening')) return true;
  if (type === 'custom-brief') {
    return order.some(isCustomBriefQuestionSectionId)
      || order.includes('customBriefQuestions')
      || order.includes(sentinel);
  }
  return order.includes(sentinel);
}
